import React, { useRef } from "react";
import { Box, Button, IconButton, ListItemIcon, ListItemText, Menu } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DragHandle } from "@mui/icons-material";
import { Form } from "react-final-form";

import { MenuItem } from "@/features/admin/components";
import { useAdminDashboard } from "@/features/admin/hooks";
import { useUpdateNavigationElementMutation } from "@/features/admin/features/pages-management/mutations";
import { StyledListItem } from "@/features/admin/features/pages-management/components/overview-item/styles";
import ExpandFolderButton from "@/features/admin/features/pages-management/components/overview-item/expand-folder-button";
import {
  Modal,
  ModalActions,
  ModalComponent,
  ModalContent,
  ModalTitle,
  showToast,
  TextField,
  If,
} from "@/features/shared/components";
import { useTranslation } from "@/lib/i18n";
import type { NavigationElement } from "@/lib/api/types";
import IconAvatar from "@/components/icon-avatar/icon-avatar";
import { navigationElementIcon, WARNING_PAGE_ICON } from "@/lib/navigation-element-kinds";
import { useWarningPageNavLabel } from "@/features/shared/hooks/use-warning-page-defaults";

type Props = {
  label: string;
  navigationElement: NavigationElement;
  dragAndDropEnabled: boolean;
};

const WarningPageOverviewItem = ({
  label,
  navigationElement,
  dragAndDropEnabled,
}: Props) => {
  const { t } = useTranslation();
  const navLabel = useWarningPageNavLabel();
  const dashboard = useAdminDashboard();
  const updateSettings = useUpdateNavigationElementMutation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const modalRef = useRef<ModalComponent>();
  const onSubmit = (values: { navLabel: string }) => {
    updateSettings.mutate(
      {
        dashboardId: dashboard.id,
        navigationElementId: navigationElement.id,
        data: { title: values.navLabel ?? "" },
      },
      {
        onSuccess: () => {
          showToast(t("admin.messages.page_updated"));
          modalRef.current?.close();
          setAnchorEl(null);
        },
        onError: () => {
          showToast(t("admin.dashboard_settings.warning_pages.error"));
        },
      },
    );
  };

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore: The `component` typescript error seems to be an mui issue. Works fine.
    <StyledListItem component="div" className="tree-node">
      <ExpandFolderButton isVisible={false} isOpen={false} onToggle={() => undefined} />
      <ListItemIcon sx={{ minWidth: 48, color: "black" }}>
        <IconAvatar icon={navigationElementIcon(navigationElement) ?? WARNING_PAGE_ICON} />
      </ListItemIcon>
      <ListItemText primary={label} />

      <If condition={!dragAndDropEnabled}>
        <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem
            icon={<EditIcon fontSize="small" />}
            title={t("admin.pages.overview.page_menu.edit")}
            onClick={() => modalRef.current?.open?.()}
          />
        </Menu>
        <Box sx={{ width: 40 }} />
        <Modal ref={modalRef} onClose={() => setAnchorEl(null)}>
          <Form<{ navLabel: string }>
            onSubmit={onSubmit}
            initialValues={{ navLabel: navigationElement.title }}
            render={({ handleSubmit, pristine }) => (
              <>
                <ModalTitle>
                  {t("admin.pages.overview.warning_page_dialog.title")}
                </ModalTitle>
                <ModalContent>
                  <form onSubmit={handleSubmit} noValidate>
                    <TextField
                      margin="dense"
                      name="navLabel"
                      label={t("admin.dashboard_settings.warning_pages.fields.nav_label")}
                      placeholder={navLabel({ type: navigationElement.type })}
                      required
                    />
                  </form>
                </ModalContent>
                <ModalActions>
                  <Button onClick={() => modalRef.current?.close()}>
                    {t("general.cancel")}
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={pristine || updateSettings.isPending}
                  >
                    {t("general.save")}
                  </Button>
                </ModalActions>
              </>
            )}
          />
        </Modal>
      </If>

      <If condition={dragAndDropEnabled}>
        <IconButton>
          <DragHandle />
        </IconButton>
      </If>
    </StyledListItem>
  );
};

export default WarningPageOverviewItem;
